import React, { useState, useEffect, useRef } from 'react';
import "./one.css";
import { FaBars, FaShareAlt } from 'react-icons/fa';
import ReactGA from "react-ga";

// Import separated components
import Header from './components/Header';
import Navigation from './components/Navigation';
import GameModeSwitcher from './components/GameModeSwitcher';
import EndGameModal from './components/EndGameModal';
import HowToPlayModal from './components/HowToPlayModal';
import FullCastModal from './components/FullCastModal';

const API_KEY = '28bbc79d002a6a0a4ffadafedaa036c5';
const BASE_URL = 'https://api.themoviedb.org/3';
const DAILY_MOVIE_ID = "23168";

const MovieGame = () => {
    // Game state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [correctMovieDetails, setCorrectMovieDetails] = useState({});
    const [feedback, setFeedback] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [gameOver, setGameOver] = useState(false);
    const [guesses, setGuesses] = useState(0);
    const [guessHistory, setGuessHistory] = useState([]);
    const [isEasyMode, setIsEasyMode] = useState(false);
    const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
    const [minYear, setMinYear] = useState(1990);
    const [maxYear, setMaxYear] = useState(new Date().getFullYear());
    const [isCastModalOpen, setIsCastModalOpen] = useState(false);
    const [yearSelectionStep, setYearSelectionStep] = useState(false);
    const [correctGuess, setCorrectGuess] = useState(false);
    const [showHintTable, setShowHintTable] = useState(false);
    const [gameMode, setGameMode] = useState('unlimited');
    const [showEndGameModal, setShowEndGameModal] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hintsUsed, setHintsUsed] = useState(0);
    const navRef = useRef();

    // Score and stats state
    const [totalGames, setTotalGames] = useState(() => parseInt(localStorage.getItem('totalGames')) || 0);
    const [totalWins, setTotalWins] = useState(() => parseInt(localStorage.getItem('totalWins')) || 0);
    const [currentScore, setCurrentScore] = useState(1100);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('highScore')) || 0);
    const [hints, setHints] = useState({
        director: false,
        releaseYear: false,
        genres: false,
        cast: false,
        runtime: false,
        rating: false,
        boxOffice: false,
    });

    // Constants
    const guessDeduction = 100;
    const hintDeductions = {
        cast: 400,
        director: 200,
        releaseYear: 150,
        rating: 50,
        boxOffice: 25,
        runtime: 20,
        genres: 15,
    };

    // Dark mode state and effects
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDarkMode;
    });

    useEffect(() => {
        const mediaQueryListener = (e) => setIsDarkMode(e.matches);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addListener(mediaQueryListener);
        return () => mediaQuery.removeListener(mediaQueryListener);
    }, []);

    useEffect(() => {
        document.body.classList.toggle('dark-mode', isDarkMode);
    }, [isDarkMode]);

    // Game initialization and mode changes
    useEffect(() => {
        ReactGA.initialize("G-MHGRFQTFDL");
        ReactGA.pageview(`/game-mode/${gameMode}`);
        
        const initializeGame = async () => {
            if (gameMode === 'daily') {
                await fetchDailyMovie();
            } else {
                await fetchRandomMovie();
            }
        };
        
        initializeGame();
    }, [gameMode, minYear, maxYear, isEasyMode]);

    // Menu handling
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && navRef.current && !navRef.current.contains(event.target) && !event.target.closest('.menu-button')) {
                setIsMenuOpen(false);
                document.body.style.overflow = '';
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    // Search handling
    useEffect(() => {
        if (!searchTerm) {
            setSearchResults([]);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            setIsLoading(true);
            try {
                const searchUrl = new URL(`${BASE_URL}/search/movie`);
                searchUrl.searchParams.append('api_key', API_KEY);
                searchUrl.searchParams.append('query', searchTerm);
                
                if (minYear && maxYear) {
                    searchUrl.searchParams.append('primary_release_date.gte', `${minYear}-01-01`);
                    searchUrl.searchParams.append('primary_release_date.lte', `${maxYear}-12-31`);
                }
                
                if (isEasyMode) {
                    searchUrl.searchParams.append('vote_average.gte', '7.0');
                    searchUrl.searchParams.append('vote_count.gte', '1000');
                }

                const response = await fetch(searchUrl);
                const data = await response.json();

                let filteredResults = data.results.filter(movie => {
                    const movieYear = new Date(movie.release_date).getFullYear();
                    return movieYear >= minYear && movieYear <= maxYear;
                });

                if (isEasyMode) {
                    filteredResults = filteredResults.filter(movie => movie.popularity > 20);
                }

                setSearchResults(filteredResults);
            } catch (error) {
                console.error('Error searching movies:', error);
                setSearchResults([]);
            } finally {
                setIsLoading(false);
            }
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm, minYear, maxYear, isEasyMode]);

    // High score update
    useEffect(() => {
        if (gameOver && currentScore > highScore) {
            setHighScore(currentScore);
            localStorage.setItem('highScore', currentScore.toString());
        }
    }, [gameOver, currentScore, highScore]);

    // Utility functions
    const updateGameStats = (didWin) => {
        const updatedTotalGames = totalGames + 1;
        const updatedTotalWins = didWin ? totalWins + 1 : totalWins;
        
        setTotalGames(updatedTotalGames);
        setTotalWins(updatedTotalWins);
        
        localStorage.setItem('totalGames', updatedTotalGames.toString());
        localStorage.setItem('totalWins', updatedTotalWins.toString());
    };

    const toggleMenu = () => {
        setIsMenuOpen(prev => !prev);
        document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
    };

    const toggleDarkMode = () => {
        setIsDarkMode(prev => !prev);
    };

    const closeEndGameModal = () => {
        setShowEndGameModal(false);
    };

    const handleYearRangeChangeAndCloseMenu = (startYear, endYear) => {
        setMinYear(startYear);
        setMaxYear(endYear);
        setYearSelectionStep(false);
        setIsMenuOpen(false);
        fetchRandomMovie();
    };

    // Game logic functions
    const fetchMovieDetails = async (movieId) => {
        const response = await fetch(
            `${BASE_URL}/movie/${movieId}?api_key=${API_KEY}&append_to_response=credits,release_dates`
        );
        return response.json();
    };

    const fetchFallbackMovie = async () => {
        const response = await fetch(
            `${BASE_URL}/discover/movie?api_key=${API_KEY}&sort_by=popularity.desc&vote_count.gte=100`
        );
        const data = await response.json();
        if (data.results.length > 0) {
            const fallbackMovie = data.results[0];
            return await fetchMovieDetails(fallbackMovie.id);
        }
        throw new Error('No fallback movie found');
    };

    const formatMovieDetails = (movieData) => ({
        id: movieData.id,
        title: movieData.title,
        director: movieData.credits.crew.find(member => member.job === 'Director')?.name || 'Unknown',
        releaseYear: movieData.release_date.split('-')[0],
        genres: movieData.genres.map(genre => genre.name),
        cast: movieData.credits.cast.map(actor => actor.name),
        runtime: movieData.runtime || 0,
        rating: movieData.vote_average,
        boxOffice: movieData.revenue || 0,
    });

    const fetchRandomMovie = async () => {
        setIsLoading(true);
        try {
            let found = false;
            let attempts = 0;
            const maxAttempts = 10;

            while (!found && attempts < maxAttempts) {
                attempts++;
                const randomReleaseYear = Math.floor(Math.random() * (maxYear - minYear + 1)) + minYear;
                
                const discoverUrl = new URL(`${BASE_URL}/discover/movie`);
                discoverUrl.searchParams.append('api_key', API_KEY);
                discoverUrl.searchParams.append('primary_release_year', randomReleaseYear.toString());
                
                if (isEasyMode) {
                    discoverUrl.searchParams.append('vote_average.gte', '7.0');
                    discoverUrl.searchParams.append('vote_count.gte', '1000');
                    discoverUrl.searchParams.append('sort_by', 'popularity.desc');
                } else {
                    discoverUrl.searchParams.append('vote_count.gte', '100');
                    discoverUrl.searchParams.append('sort_by', 'popularity.desc');
                }

                const response = await fetch(discoverUrl);
                const data = await response.json();

                if (data.results.length > 0) {
                    const randomIndex = Math.floor(Math.random() * Math.min(data.results.length, 10));
                    const randomMovie = data.results[randomIndex];
                    const movieDetails = await fetchMovieDetails(randomMovie.id);

                    const meetsEasyModeCriteria = !isEasyMode || (
                        isEasyMode && 
                        movieDetails.revenue >= 10000000 && 
                        movieDetails.vote_average >= 7.0 &&
                        movieDetails.vote_count >= 1000
                    );

                    if (meetsEasyModeCriteria) {
                        found = true;
                        setCorrectMovieDetails(formatMovieDetails(movieDetails));
                    }
                }
            }

            if (!found) {
                console.error('Could not find a suitable movie after maximum attempts');
                const fallbackMovie = await fetchFallbackMovie();
                setCorrectMovieDetails(formatMovieDetails(fallbackMovie));
            }
        } catch (error) {
            console.error('Error fetching random movie:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDailyMovie = async () => {
        setIsLoading(true);
        try {
            const movieDetails = await fetchMovieDetails(DAILY_MOVIE_ID);
            setCorrectMovieDetails(formatMovieDetails(movieDetails));
        } catch (error) {
            console.error('Error fetching daily movie:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const compareValues = (actual, correct) => {
        if (typeof actual === 'number' && typeof correct === 'number') {
            if (actual === correct) return `Correct (${actual})`;
            return actual < correct ? `↑ ${actual}` : `↓ ${actual}`;
        }
        return actual === correct ? `Correct (${actual})` : `Incorrect (${actual})`;
    };

    const selectMovie = async (selectedMovie) => {
        setGuesses(prev => prev + 1);
        const deduction = Math.min(100, guesses * 50);
        setCurrentScore(prev => Math.max(0, prev - deduction));

        try {
            const movieDetails = await fetchMovieDetails(selectedMovie.id);
            const formattedMovie = formatMovieDetails(movieDetails);

            if (formattedMovie.id === correctMovieDetails.id) {
                handleCorrectGuess();
                return;
            }

            const genreFeedback = formattedMovie.genres.map(genre => ({
                name: genre,
                correct: correctMovieDetails.genres.includes(genre)
            }));

            const newGuess = {
                title: formattedMovie.title,
                feedback: {
                    director: compareValues(formattedMovie.director, correctMovieDetails.director),
                    releaseYear: compareValues(parseInt(formattedMovie.releaseYear), parseInt(correctMovieDetails.releaseYear)),
                    genres: genreFeedback,
                    cast: formattedMovie.cast.slice(0, 3).join(', ') + 
                        (formattedMovie.cast.length > 3 ? '...' : ''),
                    runtime: compareValues(formattedMovie.runtime, correctMovieDetails.runtime),
                    rating: compareValues(formattedMovie.rating, correctMovieDetails.rating),
                    boxOffice: compareValues(formattedMovie.boxOffice, correctMovieDetails.boxOffice)
                }
            };

            setGuessHistory(prev => [...prev, newGuess]);
            setSearchTerm('');
            setSearchResults([]);

            // Track the guess in analytics
            ReactGA.event({
                category: 'Game',
                action: 'Made Guess',
                label: formattedMovie.title
            });

        } catch (error) {
            console.error('Error fetching movie details:', error);
            setFeedback('Error fetching movie details. Please try again.');
        }
    };

    const handleCorrectGuess = () => {
        setGameOver(true);
        setShowEndGameModal(true);
        setCorrectGuess(true);
        updateGameStats(true);
    };

    const handleGiveUp = () => {
        setGameOver(true);
        setShowEndGameModal(true);
        setCorrectGuess(false);
        setCurrentScore("N/A");
        updateGameStats(false);

        const formattedGenres = correctMovieDetails.genres.map(genreName => ({
            name: genreName,
            correct: true
        }));

        setGuessHistory(prev => [...prev, {
            title: correctMovieDetails.title,
            feedback: {
                director: `Correct: ${correctMovieDetails.director}`,
                releaseYear: `Correct: ${correctMovieDetails.releaseYear}`,
                genres: formattedGenres,
                cast: correctMovieDetails.cast.slice(0, 3).join(', ') + 
                    (correctMovieDetails.cast.length > 3 ? '...' : ''),
                runtime: `Correct: ${correctMovieDetails.runtime} minutes`,
                rating: `Correct: ${correctMovieDetails.rating}`,
                boxOffice: `Correct: $${correctMovieDetails.boxOffice.toLocaleString()}`,
            }
        }]);
    };

    const handleReset = () => {
        setSearchTerm('');
        setSearchResults([]);
        setCorrectMovieDetails({});
        setFeedback({});
        setIsLoading(true);
        setGameOver(false);
        setShowEndGameModal(false);
        setGuesses(0);
        setGuessHistory([]);
        setCorrectGuess(false);
        setCurrentScore(1100);
        setHints({
            director: false,
            releaseYear: false,
            genres: false,
            cast: false,
            runtime: false,
            rating: false,
            boxOffice: false,
        });
        setShowHintTable(false);
        setHintsUsed(0);
        fetchRandomMovie();
    };

    const revealHint = (hintType) => {
        if (!hints[hintType]) {
            setHints(prev => ({ ...prev, [hintType]: true }));
            setCurrentScore(prev => prev - hintDeductions[hintType]);
            setHintsUsed(prev => prev + 1);
        }
    };

    // Share functionality
    const encodeGameState = () => {
        const gameState = `${guessHistory.length},${correctMovieDetails.id}`;
        return `${window.location.origin}${window.location.pathname}?gameState=${btoa(gameState)}`;
    };

    const shareResults = () => {
        const gameStateUrl = encodeGameState();
        const resultsSummary = `I played MovieQuest and ${correctGuess ? 'guessed' : 'tried to guess'} the movie in ${guesses} guesses with ${hintsUsed} hints used.\n\nScore: ${currentScore}\nMovie: ${correctMovieDetails.title}\n\nTry to beat my score! ${gameStateUrl} @crossovergrid 🎬🎉`;

        navigator.clipboard.writeText(resultsSummary).then(() => {
            ReactGA.event({
                category: 'Social',
                action: 'Share Results',
                label: correctMovieDetails.title
            });
            alert("Game summary copied to clipboard!");
        }).catch(err => {
            console.error("Failed to copy: ", err);
        });
    };

    // Load game state from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const gameStateParam = urlParams.get('gameState');
        
        if (gameStateParam) {
            try {
                const decodedState = atob(gameStateParam);
                const [guessCount, movieId] = decodedState.split(',');
                
                // Track shared game loads
                ReactGA.event({
                    category: 'Game',
                    action: 'Load Shared Game',
                    label: movieId
                });

                // Initialize game with shared movie
                const loadSharedMovie = async () => {
                    const movieDetails = await fetchMovieDetails(movieId);
                    setCorrectMovieDetails(formatMovieDetails(movieDetails));
                };
                
                loadSharedMovie();
            } catch (error) {
                console.error('Error loading shared game:', error);
            }
        }
    }, []);

    // Track game events
    useEffect(() => {
        if (gameOver) {
            ReactGA.event({
                category: 'Game',
                action: correctGuess ? 'Win' : 'Give Up',
                label: `Guesses: ${guesses}, Hints: ${hintsUsed}`,
                value: currentScore
            });
        }
    }, [gameOver]);

    // Persist game state
    useEffect(() => {
        const gameState = {
            currentScore,
            highScore,
            totalGames,
            totalWins,
            isEasyMode,
            isDarkMode
        };
        localStorage.setItem('movieQuestState', JSON.stringify(gameState));
    }, [currentScore, highScore, totalGames, totalWins, isEasyMode, isDarkMode]);

    // Load persisted game state
    useEffect(() => {
        const savedState = localStorage.getItem('movieQuestState');
        if (savedState) {
            const {
                currentScore: savedScore,
                highScore: savedHighScore,
                totalGames: savedTotalGames,
                totalWins: savedTotalWins,
                isEasyMode: savedIsEasyMode,
                isDarkMode: savedIsDarkMode
            } = JSON.parse(savedState);

            setCurrentScore(savedScore || 1100);
            setHighScore(savedHighScore || 0);
            setTotalGames(savedTotalGames || 0);
            setTotalWins(savedTotalWins || 0);
            setIsEasyMode(savedIsEasyMode || false);
            setIsDarkMode(savedIsDarkMode || false);
        }
    }, []);

    // Render section
    return (
        <div className={`game-container ${isDarkMode ? 'dark-mode' : ''}`}>
            <Header 
                isMenuOpen={isMenuOpen}
                onMenuClick={() => setIsMenuOpen(!isMenuOpen)}
                gameMode={gameMode}
            />

            <Navigation 
                isMenuOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
                minYear={minYear}
                maxYear={maxYear}
                onYearRangeChange={handleYearRangeChangeAndCloseMenu}
                isEasyMode={isEasyMode}
                toggleEasyMode={setIsEasyMode}
                yearSelectionStep={yearSelectionStep}
                setYearSelectionStep={setYearSelectionStep}
            />

            <div className="game-content">
                <div className="scoreboard">
                    <div className="score-item">
                        <span>Score</span>
                        <span>{currentScore}</span>
                    </div>
                    <div className="score-item">
                        <span>High Score</span>
                        <span>{highScore}</span>
                    </div>
                    <div className="score-item">
                        <span>Guesses</span>
                        <span>{guesses}</span>
                    </div>
                </div>

                <div className="search-section">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search for a movie..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={gameOver}
                    />
                    {isLoading && <div className="loading">Loading...</div>}
                </div>

                {searchResults.length > 0 && !gameOver && (
                    <div className="search-results">
                        {searchResults.map((movie) => (
                            <div
                                key={movie.id}
                                className="movie-card"
                                onClick={() => selectMovie(movie)}
                            >
                                <h3>{movie.title}</h3>
                                <p>{movie.releaseYear}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="guess-history">
                    <table>
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Director</th>
                                <th>Year</th>
                                <th>Genres</th>
                                <th>Cast</th>
                                <th>Runtime</th>
                                <th>Rating</th>
                                <th>Box Office</th>
                            </tr>
                        </thead>
                        <tbody>
                            {guessHistory.map((guess, index) => (
                                <tr key={index}>
                                    <td className={guess.title === correctMovieDetails.title ? 'correct' : 'incorrect'}>
                                        {guess.title}
                                    </td>
                                    <td className={guess.feedback.director.startsWith('Correct') ? 'correct' : 'incorrect'}>
                                        {guess.feedback.director}
                                    </td>
                                    <td className={guess.feedback.releaseYear.startsWith('Correct') ? 'correct' : 'incorrect'}>
                                        {guess.feedback.releaseYear}
                                    </td>
                                    <td>
                                        {guess.feedback.genres.map((genre, i) => (
                                            <span 
                                                key={i} 
                                                className={`genre-tag ${genre.correct ? 'correct' : 'incorrect'}`}
                                            >
                                                {genre.name}
                                                {i < guess.feedback.genres.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                    </td>
                                    <td className={guess.feedback.cast.startsWith('Matched') ? 'correct' : 'incorrect'}>
                                        {guess.feedback.cast}
                                    </td>
                                    <td className={guess.feedback.runtime.startsWith('Correct') ? 'correct' : 'incorrect'}>
                                        {guess.feedback.runtime}
                                    </td>
                                    <td className={guess.feedback.rating.startsWith('Correct') ? 'correct' : 'incorrect'}>
                                        {guess.feedback.rating}
                                    </td>
                                    <td className={
                                        guess.feedback.boxOffice.startsWith('Correct') ? 'correct' :
                                        guess.feedback.boxOffice.includes('↑') || guess.feedback.boxOffice.includes('↓') ? 'incorrect' : ''
                                    }>
                                        {guess.feedback.boxOffice}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="game-controls">
                    {!gameOver && (
                        <>
                            <button 
                                className="control-button hint-button"
                                onClick={() => setShowHintTable(!showHintTable)}
                            >
                                Get Hints
                            </button>
                            <button 
                                className="control-button give-up-button"
                                onClick={handleGiveUp}
                            >
                                Give Up
                            </button>
                        </>
                    )}
                    {gameOver && (
                        <button 
                            className="control-button play-again-button"
                            onClick={handleReset}
                        >
                            Play Again
                        </button>
                    )}
                </div>

                {showHintTable && !gameOver && (
                    <div className="hint-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Cast (-400)</th>
                                    <th>Director (-200)</th>
                                    <th>Release Year (-150)</th>
                                    <th>Rating (-50)</th>
                                    <th>Box Office (-25)</th>
                                    <th>Runtime (-20)</th>
                                    <th>Genres (-15)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td onClick={() => revealHint('cast')}>
                                        {hints.cast ? correctMovieDetails.cast.slice(0, 3).join(', ') : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('director')}>
                                        {hints.director ? correctMovieDetails.director : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('releaseYear')}>
                                        {hints.releaseYear ? correctMovieDetails.releaseYear : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('rating')}>
                                        {hints.rating ? correctMovieDetails.rating : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('boxOffice')}>
                                        {hints.boxOffice ? `$${correctMovieDetails.boxOffice.toLocaleString()}` : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('runtime')}>
                                        {hints.runtime ? `${correctMovieDetails.runtime} min` : 'Reveal'}
                                    </td>
                                    <td onClick={() => revealHint('genres')}>
                                        {hints.genres ? correctMovieDetails.genres.join(', ') : 'Reveal'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <EndGameModal
                isOpen={showEndGameModal}
                onClose={() => setShowEndGameModal(false)}
                onPlayAgain={handleReset}
                correctGuess={correctGuess}
                guesses={guesses}
                score={currentScore}
                correctMovie={correctMovieDetails}
            />

            <HowToPlayModal
                isOpen={isHowToPlayOpen}
                onClose={() => setIsHowToPlayOpen(false)}
            />

            <FullCastModal
                isOpen={isCastModalOpen}
                onClose={() => setIsCastModalOpen(false)}
                cast={correctMovieDetails.cast || []}
            />
        </div>
    );
};

export default MovieGame; 